// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useTranslation } from 'react-i18next';
import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Typography,
} from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { Input } from '@shared/ui-kit/components/input';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useTagManagement } from '@shared/tag-management';
import type { TagEntity } from '@shared/data-access/interfaces/redux/tags-management-state-types';

export interface TagManagementProps {
  objectType: string;
  objectId: string;
  allTags: readonly TagEntity[];
  initialAssignedTags: readonly TagEntity[];
  onChange?: (tagIds: number[]) => void;
  onCreateTag?: (name: string) => void;
}

const useTagManagementSetup = (props: TagManagementProps) => {
  const { t } = useTranslation();

  const setup = useTagManagement(props);

  return { ...setup, t };
};

export const TagManagement = defineComponent({
  displayName: 'TagManagement',
  useSetup: useTagManagementSetup,
  View: ({
    assignedTags,
    value,
    setValue,
    open,
    setOpen,
    filteredTags,
    canCreate,
    showDropdown,
    onAddTag,
    onRemoveTag,
    onConfirmCreate,
    t,
  }) => (
    <>
      <Typography
        variant="subtitle2"
        className="!mb-2 !text-base font-semibold uppercase !text-primary-medium"
      >
        <Translation i18nKey="aggregator.tags" ns="aggregator" />
      </Typography>

      <div className="mb-2 flex flex-wrap gap-3">
        {assignedTags.map(tag => (
          <Chip
            key={tag.tag_id}
            label={tag.name}
            color="primary"
            variant="filled"
            size="small"
            onDelete={() => onRemoveTag(tag.tag_id)}
            data-testid={`tag-chip-${tag.tag_id}`}
          />
        ))}
      </div>

      <div className="relative w-full">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <AppIcon icon="Search" color="disabled" size="base" />
          </span>
          <Input
            type="text"
            autoComplete="off"
            className="w-full pl-10"
            placeholder={t('aggregator.add-tag', { ns: 'aggregator' })}
            value={value}
            onChange={e => setValue(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            data-testid="tag-search-input"
          />
        </div>
        {showDropdown && (
          <Paper
            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto shadow-lg"
            elevation={3}
          >
            <List disablePadding dense>
              {filteredTags.map((tag: TagEntity) => (
                <ListItem key={tag.tag_id} disablePadding>
                  <ListItemButton onClick={() => onAddTag(tag)}>
                    {tag.name}
                  </ListItemButton>
                </ListItem>
              ))}
              {canCreate && (
                <ListItem disablePadding>
                  <ListItemButton onClick={onConfirmCreate}>
                    <AppIcon
                      icon="Plus"
                      color="inherit"
                      size="base"
                      className="mr-2"
                    />
                    {t('aggregator.create-new-tag', {
                      ns: 'aggregator',
                      name: value.trim(),
                    })}
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </div>
    </>
  ),
});
